import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, TrendingUp, Bot } from "lucide-react";
import { Logo } from "@/components/Logo";

export default function Landing() {
  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      <header className="flex items-center justify-between border-b border-border px-6 py-4 lg:px-12">
        <Logo to="/" size={26} testid="landing-logo-link" />
        <div className="flex items-center gap-2">
          <Link to="/login" className="iso-btn-ghost" data-testid="landing-login-btn">Sign in</Link>
          <Link to="/register" className="iso-btn-primary" data-testid="landing-register-btn">
            Get started <ArrowRight className="size-3.5" />
          </Link>
        </div>
      </header>

      <section className="mx-auto max-w-6xl px-6 py-16 lg:py-24">
        <div className="grid items-center gap-8 lg:grid-cols-[1.2fr,1fr]">
          <div>
            <div className="iso-chip-secondary iso-chip">
              <Sparkles className="size-3" /> Monte Carlo · 14-factor self-learning
            </div>
            <h1 className="mt-5 font-lexend text-4xl font-bold leading-tight text-gray-900 sm:text-5xl lg:text-[56px]">
              Play the game<br />
              <span className="text-secondary">before it's played.</span>
            </h1>
            <p className="mt-5 max-w-xl text-base text-gray-500">
              A professional sports prediction cockpit — 10,000-iteration simulations,
              closing-line value tracking, Kelly sizing, and an AI analyst that only speaks
              when the math does.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" className="iso-btn-primary" data-testid="hero-cta">
                Create free account <ArrowRight className="size-4" />
              </Link>
              <Link to="/login" className="iso-btn-outline">
                Try demo <span className="ml-1 font-mono text-[10px] text-gray-400">demo@gameedge.io</span>
              </Link>
            </div>
          </div>
          <div className="relative hidden lg:block">
            <div className="absolute inset-0 -z-10 rounded-full bg-gradient-radial from-secondary/20 to-transparent blur-3xl" />
            <img
              src="/gameguy.png"
              alt="GameEdge mascot"
              className="mx-auto w-full max-w-md drop-shadow-[0_20px_40px_rgba(78,54,245,0.25)] animate-[float_6s_ease-in-out_infinite]"
              draggable={false}
              data-testid="hero-mascot"
            />
          </div>
        </div>

        <div className="mt-16 grid gap-5 md:grid-cols-3">
          <Feature icon={Sparkles} title="Monte Carlo engine"
            body="10k+ iterations per game. Projected scores, spread cover, total over/under — every market priced against the model." />
          <Feature icon={TrendingUp} title="Expected value edges"
            body="EV and Kelly% shown alongside market odds. Track every pick and let ROI compound." />
          <Feature icon={Bot} title="AI edge analyst"
            body="Grounded in your simulation results. No hallucinated injuries. Claude Sonnet 4.5 under the hood." />
        </div>
      </section>
    </div>
  );
}

const Feature = ({ icon: Icon, title, body }) => (
  <div className="iso-card p-6 transition-shadow hover:shadow-card-lg">
    <div className="flex size-10 items-center justify-center rounded-xl bg-primary text-primary-foreground">
      <Icon className="size-5" />
    </div>
    <h3 className="mt-4 font-lexend">{title}</h3>
    <p className="mt-1.5 text-sm text-gray-500">{body}</p>
  </div>
);
